import dayjs from 'dayjs';
import * as yup from 'yup';

import {
  isOvernightServiceSelected,
  isRwbServiceSelected,
} from '~/components/SearchFiltersForm/utils';

import { normalizeText } from './app';

yup.addMethod(yup.StringSchema, 'wordsBetween', function (minWords, maxWords, message) {
  return this.test(
    'wordsBetween',
    message || `Please enter between ${minWords} and ${maxWords} words`,

    function (value: string) {
      const wordCount = value ? value.trim().split(/\s+/).length : 0;

      return wordCount >= minWords && wordCount <= maxWords;
    }
  );
});

export const locationValidationObject = yup.object({
  address: yup.object({
    lat: yup.number().required(),
    lng: yup.number().required(),
    postcode: yup.string().required(),
    stateName: yup.string().required(),
    suburb: yup.string().required(),
  }),
});

export const serviceValidationObject = yup.object({
  type: yup
    .string()
    .test('length', 'Please choose pet service type', (service) => {
      if (!service) {
        return false;
      }

      return service.toString().length > 0;
    })
    .required(),
});

export const petTypesValidationObject = yup
  .object({
    puppy: yup.string().nullable(),
    smallDog: yup.string().nullable(),
    mediumDog: yup.string().nullable(),
    largeDog: yup.string().nullable(),
    giantDog: yup.string().nullable(),
    cat: yup.string().nullable(),
    creature: yup.string().nullable(),
    bird: yup.string().nullable(),
  })
  .test('required-validation', 'Pets required', (petTypes) =>
    Object.values(petTypes || {}).some((value) => value !== '0' && value != null && value !== '')
  );

export const expandedPetsValidationObject = yup
  .array()
  .of(
    yup.object().shape({
      type: yup.string().required('Type is required').min(1, 'Type cannot be empty'),
    })
  )
  .min(1, 'You must provide at least one pet');

export const chronologyValidationObject = yup
  .object({
    endDate: yup.string().nullable(),
    rwbStartDate: yup.string().nullable(),
    scheduledDates: yup.array().of(yup.string().required()),
    startDate: yup.string().nullable(),
    weekDays: yup.object({
      friday: yup.boolean().required(),
      monday: yup.boolean().required(),
      saturday: yup.boolean().required(),
      sunday: yup.boolean().required(),
      thursday: yup.boolean().required(),
      tuesday: yup.boolean().required(),
      wednesday: yup.boolean().required(),
    }),
  })
  .test('required-validation', 'Dates required', function (chronology) {
    const { endDate, startDate, scheduledDates, rwbStartDate, weekDays } = chronology;

    // reading service and bookingType from yup.ref to calculate validation
    const serviceRef: { type: string } = this.resolve(yup.ref('service'));
    const bookingTypeRef: string = this.resolve(yup.ref('bookingType'));

    // following conditions are taken from DateSelector.tsx

    /*
     * if one of the overnight service is selected
     * then start and end dates should not be empty
     */
    if (
      isOvernightServiceSelected(serviceRef.type) &&
      endDate !== undefined &&
      startDate !== undefined &&
      endDate !== '' &&
      startDate !== ''
    ) {
      return true;
    }

    /*
     * if one of the rwb service is selected and booking type is rwb
     * then rwbStartDate and any weekdays should not be empty
     */
    if (
      isRwbServiceSelected(serviceRef.type, bookingTypeRef) &&
      rwbStartDate !== undefined &&
      rwbStartDate !== '' &&
      Object.values(weekDays || {}).some((value) => value !== false) !== false
    ) {
      return true;
    }

    /*
     * if any of above scenarios not matched
     * scheduledDates should not be empty
     */
    if (
      !isRwbServiceSelected(serviceRef.type, bookingTypeRef) &&
      !isOvernightServiceSelected(serviceRef.type) &&
      scheduledDates !== undefined &&
      scheduledDates.length > 0
    ) {
      return true;
    }

    return false;
  });

export const isValidCurrentDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return false;
  } // Invalid date check

  // Get today's date at midnight (ignoring time)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return date.getTime() >= today.getTime();
};

export const isValidCurrentWeekStartDay = (dateString: string): boolean => {
  const date = new Date(dateString);

  if (!!isNaN(date.getTime()) || date.getTime() < Date.now()) {
    return false;
  }

  const day = date.getDay();

  return day === 1;
};

export const phoneValidationSchema = yup
  .string()
  .required('Please enter your phone number')
  .matches(/^04\d{2}\s\d{3}\s\d{3}$/, 'Enter a valid mobile number (04XX XXX XXX).');

export const phoneGeographicValidationSchema = yup
  .string()
  .required('Please enter your phone number')
  .test('australian-phone', 'Enter a valid Australian phone number', (value) => {
    if (!value) {
      return false;
    }

    const phone = value.replace(/\s/g, '');

    return /^(?:04\d{8}|0[2378]\d{8})$/.test(phone);
  });

const cleanInput = (input: string): string => {
  if (!input) {
    return '';
  }
  return normalizeText(input)
    .replace(/[^a-z\s]/g, '') // Remove special characters (keep only letters and spaces)
    .replace(/\s+/g, ' ') // Convert multiple spaces to a single space
    .trim(); // Trim leading/trailing spaces
};

const containsBlockedWords = (input: string): boolean => {
  if (!input) {
    return false;
  }

  const normalizedInput = cleanInput(input);

  if (/^\s*mp\s*$/i.test(normalizedInput)) {
    return true;
  } // Reject "mp" alone
  if (/\bmad\s?paws\b|\bmadapaws\b/i.test(normalizedInput)) {
    return true;
  } // Reject "mad paws" or "madapaws"

  return false;
};

export const firstNameValidationSchema = yup
  .string()
  .required('Please enter your first name')
  .test('no-mad-paws-or-mp', 'Invalid name, please try again.', (value) => {
    if (!value) {
      return true;
    } // Allow empty values (handled by required)
    return !containsBlockedWords(value);
  });

export const lastNameValidationSchema = yup
  .string()
  .required('Please enter your last name')
  .test('no-mad-paws-or-mp', 'Invalid name, please try again.', (value) => {
    if (!value) {
      return true;
    } // Allow empty values (handled by required)
    return !containsBlockedWords(value);
  });

export const birthdayValidationSchema = yup
  .string()
  .required('This is a mandatory field')
  .test('is-valid-date', 'Please select a valid date', (value) => {
    const date = value ? dayjs(value) : null;
    return Boolean(date?.isValid() && date.format('YYYY-MM-DD') === value);
  })
  .test('is-adult', 'Must be 18 years of age or older', (value) =>
    Boolean(value && dayjs().diff(dayjs(value), 'year') >= 18)
  );
